import { create } from 'zustand';
import i18n from 'i18next';

// 모달 타입
type ModalVariant = 'primary' | 'secondary' | 'danger';

// 모달 정보
interface ModalInfo {
  id: string;
  title: string;
  message: string;
  onConfirm?: () => void;
  onCancel?: () => void;
  confirmText?: string;
  cancelText?: string;
  variant?: ModalVariant;
  showCancel?: boolean;
  /** 입력해야 확인 버튼이 활성화되는 정확한 문자열 (예: 회사명) */
  confirmKeyword?: string;
  /** 입력란 위에 표시되는 안내 문구 */
  confirmPrompt?: string;
}

interface TypedConfirmOptions {
  title: string;
  message: string;
  /** 사용자가 정확히 입력해야 하는 문자열 (예: 회사명/사이트명) */
  confirmKeyword: string;
  onConfirm: () => void;
  onCancel?: () => void;
  variant?: ModalVariant;
  confirmText?: string;
  cancelText?: string;
  /** 입력란 위 안내 문구 */
  confirmPrompt?: string;
}

interface ModalState {
  currentModal: ModalInfo | null;
  showConfirm: (
    title: string,
    message: string,
    onConfirm: () => void,
    onCancel?: () => void,
    variant?: ModalVariant,
    confirmText?: string,
    cancelText?: string
  ) => void;
  showTypedConfirm: (options: TypedConfirmOptions) => void;
  showAlert: (
    title: string,
    message: string,
    onConfirm?: () => void,
    variant?: ModalVariant
  ) => void;
  hideModal: () => void;
}

export const useModalStore = create<ModalState>(set => ({
  currentModal: null,

  showConfirm: (
    title,
    message,
    onConfirm,
    onCancel,
    variant = 'primary',
    confirmText,
    cancelText
  ) => {
    // ZUSTAND: 확인/취소 모달 표시
    const id = `modal-${Date.now()}`;
    set({
      currentModal: {
        id,
        title,
        message,
        onConfirm,
        onCancel,
        variant,
        confirmText: confirmText || i18n.t('config:confirm'),
        cancelText: cancelText || i18n.t('config:cancel'),
        showCancel: true,
      },
    });
  },

  showTypedConfirm: options => {
    // ZUSTAND: 이름 타이핑 확인 모달 표시 (파괴적 작업용)
    const id = `modal-${Date.now()}`;
    set({
      currentModal: {
        id,
        title: options.title,
        message: options.message,
        onConfirm: options.onConfirm,
        onCancel: options.onCancel,
        variant: options.variant || 'danger',
        confirmText: options.confirmText || i18n.t('config:confirm'),
        cancelText: options.cancelText || i18n.t('config:cancel'),
        showCancel: true,
        confirmKeyword: options.confirmKeyword,
        confirmPrompt: options.confirmPrompt,
      },
    });
  },

  showAlert: (title, message, onConfirm, variant = 'primary') => {
    // ZUSTAND: 알림 모달 표시
    const id = `modal-${Date.now()}`;
    set({
      currentModal: {
        id,
        title,
        message,
        onConfirm: onConfirm || (() => {}),
        variant,
        confirmText: i18n.t('config:confirm'),
        showCancel: false,
      },
    });
  },

  hideModal: () => {
    // ZUSTAND: 모달 닫기
    set({ currentModal: null });
  },
}));

// Development helper
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  (window as any).__MODAL_STORE__ = useModalStore;

  (window as any).getModalState = () => {
    const state = useModalStore.getState();
    return state;
  };

  (window as any).watchModalStore = () => {
    return useModalStore.subscribe(state => {
      console.log('🔄 [Modal Store Update]', {
        modalOpen: !!state.currentModal,
        modalTitle: state.currentModal?.title,
      });
    });
  };

  // console.log('🛠️ Modal Store Debug Tools Available:');
  // console.log('  - getModalState() : 현재 모달 상태 확인');
  // console.log('  - watchModalStore() : 실시간 변화 감지');
  // console.log('  - __MODAL_STORE__.getState() : 전체 상태');
}
